import { type Amount, addAmounts, divideByNumber, multiplyByNumber, newAmount } from '@orus.eu/amount'
import type { PaymentRecurrence } from '@orus.eu/quote'

import type { InsuranceOutput } from '../insurance-output.ts'
import type { QuoteV3 } from '../quote.ts'

import { getInsuranceOutputAmount, monthsInAYear } from './orus-pricing-method.ts'

export class QuoteV3Calculator {
  private readonly quote: QuoteV3

  constructor(quote: QuoteV3) {
    this.quote = quote
  }

  private shouldSkipInsuranceOutput(
    insuranceOutput: InsuranceOutput,
    isContractFirstMonth: boolean,
    isAnnuityFirstMonth: boolean,
  ): boolean {
    return (
      (insuranceOutput.paymentTerms.type === 'oneTime' && !isContractFirstMonth) ||
      (insuranceOutput.paymentTerms.type === 'yearly' && !isAnnuityFirstMonth) ||
      (insuranceOutput.optionality.type === 'optional' && !insuranceOutput.optionality.isActive) ||
      insuranceOutput.optionality.type === 'excluded'
    )
  }
  getFirstPaymentAmount({ paymentRecurrence }: { paymentRecurrence: PaymentRecurrence }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1:
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getMonthlyTotalAmountForPaymentRecurrence({
              paymentRecurrence,
              isAnnuityFirstMonth: true,
              isContractFirstMonth: true,
            })
          }
          case 'yearly': {
            return this.getYearlyTotalAmountForPaymentRecurrence({
              paymentRecurrence,
              isContractFirstMonth: true,
              isAnnuityFirstMonth: true,
            })
          }
        }
    }
  }

  getYearlyTotalAmountForPaymentRecurrence({
    paymentRecurrence,
    isContractFirstMonth,
    isAnnuityFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isContractFirstMonth: boolean
    isAnnuityFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: getInsuranceOutputAmount,
        })
      }
    }
  }

  /**
   * Returns the yearly amount as it is actually billed for the given payment recurrence.
   *
   * For a monthly recurrence, the customer is billed 12 rounded monthly installments
   * (first month + 11 subsequent months), so the effective yearly amount is
   * `firstMonth + 11 × subsequentMonth`. This can differ by a few cents from
   * `getYearlyTotalAmountForPaymentRecurrence` (which sums the yearly per-component amounts once),
   * because each monthly installment is rounded to the cent independently.
   *
   * Use this for amounts displayed to the customer (agreed terms) so they match the price
   * shown in the BO/checkout and actually charged. Keep `getYearlyTotalAmountForPaymentRecurrence`
   * for billing and internal computations.
   */
  getEffectiveYearlyAmountForPaymentRecurrence({
    paymentRecurrence,
    getInsuranceOutputAmount,
  }: {
    paymentRecurrence: PaymentRecurrence
    getInsuranceOutputAmount: (insuranceOutput: InsuranceOutput) => Amount
  }): Amount {
    switch (paymentRecurrence) {
      case 'monthly':
        return addAmounts(
          this.getMonthlyAmountForMonthlyRecurrence({
            isAnnuityFirstMonth: true,
            isContractFirstMonth: false,
            getInsuranceOutputAmount,
          }),
          multiplyByNumber(
            this.getMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth: false,
              isContractFirstMonth: false,
              getInsuranceOutputAmount,
            }),
            monthsInAYear - 1,
          ),
        )
      case 'yearly':
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth: true,
          isContractFirstMonth: false,
          getInsuranceOutputAmount,
        })
    }
  }

  getEffectiveYearlyTotalAmountForPaymentRecurrence({
    paymentRecurrence,
  }: {
    paymentRecurrence: PaymentRecurrence
  }): Amount {
    return this.getEffectiveYearlyAmountForPaymentRecurrence({
      paymentRecurrence,
      getInsuranceOutputAmount: getInsuranceOutputAmount,
    })
  }

  getEffectiveYearlyTotalExTaxAmountForPaymentRecurrence({
    paymentRecurrence,
  }: {
    paymentRecurrence: PaymentRecurrence
  }): Amount {
    return this.getEffectiveYearlyAmountForPaymentRecurrence({
      paymentRecurrence,
      getInsuranceOutputAmount: this.getInsuranceOutputExTaxAmount,
    })
  }

  getEffectiveYearlyTotalInsuredExTaxAmountForPaymentRecurrence({
    paymentRecurrence,
  }: {
    paymentRecurrence: PaymentRecurrence
  }): Amount {
    return this.getEffectiveYearlyAmountForPaymentRecurrence({
      paymentRecurrence,
      getInsuranceOutputAmount: this.getInsuranceOutputInsuredExTaxAmount,
    })
  }

  getEffectiveYearlyTotalTaxesAmountForPaymentRecurrence({
    paymentRecurrence,
  }: {
    paymentRecurrence: PaymentRecurrence
  }): Amount {
    return this.getEffectiveYearlyAmountForPaymentRecurrence({
      paymentRecurrence,
      getInsuranceOutputAmount: this.getInsuranceOutputTaxesAmount,
    })
  }

  getEffectiveYearlyOrusManagementFeeAmountForPaymentRecurrence({
    paymentRecurrence,
  }: {
    paymentRecurrence: PaymentRecurrence
  }): Amount {
    return this.getEffectiveYearlyAmountForPaymentRecurrence({
      paymentRecurrence,
      getInsuranceOutputAmount: this.getInsuranceOutputOrusManagementFeeAmount,
    })
  }

  getEffectiveYearlyOrusInstallmentFeeAmountForPaymentRecurrence({
    paymentRecurrence,
  }: {
    paymentRecurrence: PaymentRecurrence
  }): Amount {
    return this.getEffectiveYearlyAmountForPaymentRecurrence({
      paymentRecurrence,
      getInsuranceOutputAmount: this.getInsuranceOutputInstallmentFeeAmount,
    })
  }

  getEffectiveYearlyPartnerManagementFeeAmountForPaymentRecurrence({
    paymentRecurrence,
  }: {
    paymentRecurrence: PaymentRecurrence
  }): Amount {
    return this.getEffectiveYearlyAmountForPaymentRecurrence({
      paymentRecurrence,
      getInsuranceOutputAmount: this.getInsuranceOutputPartnerManagementFeeAmount,
    })
  }

  getEffectiveYearlyOrusDiscountAmountForPaymentRecurrence({
    paymentRecurrence,
  }: {
    paymentRecurrence: PaymentRecurrence
  }): Amount {
    return this.getEffectiveYearlyAmountForPaymentRecurrence({
      paymentRecurrence,
      getInsuranceOutputAmount: this.getInsuranceOutputOrusDiscountAmount,
    })
  }

  getInsuranceOutputsYearlyTotalAmount({
    isContractFirstMonth,
    isAnnuityFirstMonth,
    insuranceOutputs,
  }: {
    isContractFirstMonth: boolean
    isAnnuityFirstMonth: boolean
    insuranceOutputs: InsuranceOutput[]
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getInsuranceOutputsYearlyAmountForPaymentRecurrence({
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: getInsuranceOutputAmount,
          insuranceOutputs,
        })
      }
    }
  }

  /**
   * Returns the base premium amount for the given insurance-outputs, including taxes
   * but excluding fees and discounts
   */
  getInsuranceOutputsYearlyBasePremiumWithTaxesAmount({
    isContractFirstMonth,
    isAnnuityFirstMonth,
    insuranceOutputs,
  }: {
    isContractFirstMonth: boolean
    isAnnuityFirstMonth: boolean
    insuranceOutputs: InsuranceOutput[]
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getInsuranceOutputsYearlyAmountForPaymentRecurrence({
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: (insuranceOutput) =>
            getInsuranceOutputAmount(insuranceOutput, { type: 'basePremiumWithTaxes' }),
          insuranceOutputs,
        })
      }
    }
  }

  getMonthlyTotalAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: getInsuranceOutputAmount,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getYearlyTotalAmountForPaymentRecurrence({
                paymentRecurrence,
                isContractFirstMonth,
                isAnnuityFirstMonth,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }

  getInsuranceOutputsMonthlyTotalAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
    insuranceOutputs,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
    insuranceOutputs: InsuranceOutput[]
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getInsuranceOutputsMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: getInsuranceOutputAmount,
              insuranceOutputs,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getInsuranceOutputsYearlyTotalAmount({
                isContractFirstMonth,
                isAnnuityFirstMonth,
                insuranceOutputs,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }

  getMonthlyTotalExTaxAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: this.getInsuranceOutputExTaxAmount,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getYearlyTotalExTaxAmountForPaymentRecurrence({
                paymentRecurrence,
                isAnnuityFirstMonth,
                isContractFirstMonth,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }

  getInsuranceOutputsMonthlyTotalExTaxAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
    insuranceOutputs,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
    insuranceOutputs: InsuranceOutput[]
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getInsuranceOutputsMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: this.getInsuranceOutputExTaxAmount,
              insuranceOutputs,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getInsuranceOutputsYearlyTotalExTaxAmount({
                isAnnuityFirstMonth,
                isContractFirstMonth,
                insuranceOutputs,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }

  getYearlyTotalExTaxAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: this.getInsuranceOutputExTaxAmount,
        })
      }
    }
  }

  getInsuranceOutputsYearlyTotalExTaxAmount({
    isAnnuityFirstMonth,
    isContractFirstMonth,
    insuranceOutputs,
  }: {
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
    insuranceOutputs: InsuranceOutput[]
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getInsuranceOutputsYearlyAmountForPaymentRecurrence({
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: this.getInsuranceOutputExTaxAmount,
          insuranceOutputs,
        })
      }
    }
  }

  getMonthlyTotalInsuredExTaxAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: this.getInsuranceOutputInsuredExTaxAmount,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getYearlyTotalInsuredExTaxAmountForPaymentRecurrence({
                paymentRecurrence,
                isAnnuityFirstMonth,
                isContractFirstMonth,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }

  getYearlyTotalInsuredExTaxAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: this.getInsuranceOutputInsuredExTaxAmount,
        })
      }
    }
  }

  getMonthlyTotalTaxesAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: this.getInsuranceOutputTaxesAmount,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getYearlyTotalTaxesAmountForPaymentRecurrence({
                paymentRecurrence,
                isAnnuityFirstMonth,
                isContractFirstMonth,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }

  getYearlyTotalTaxesAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: this.getInsuranceOutputTaxesAmount,
        })
      }
    }
  }

  getMonthlyOrusInstallmentFeeAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: this.getInsuranceOutputInstallmentFeeAmount,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getYearlyOrusInstallmentFeeAmountForPaymentRecurrence({
                paymentRecurrence,
                isAnnuityFirstMonth,
                isContractFirstMonth,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }

  getYearlyOrusInstallmentFeeAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: this.getInsuranceOutputInstallmentFeeAmount,
        })
      }
    }
  }

  getMonthlyPartnerManagementFeeAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: this.getInsuranceOutputPartnerManagementFeeAmount,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getYearlyPartnerManagementFeeAmountForPaymentRecurrence({
                paymentRecurrence,
                isAnnuityFirstMonth,
                isContractFirstMonth,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }

  getYearlyPartnerManagementFeeAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: this.getInsuranceOutputPartnerManagementFeeAmount,
        })
      }
    }
  }

  getYearlyOrusManagementFeeAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: this.getInsuranceOutputOrusManagementFeeAmount,
        })
      }
    }
  }

  getMonthlyOrusManagementFeeAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: this.getInsuranceOutputOrusManagementFeeAmount,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getYearlyPartnerManagementFeeAmountForPaymentRecurrence({
                paymentRecurrence,
                isAnnuityFirstMonth,
                isContractFirstMonth,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }

  getYearlyOrusDiscountAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: this.getInsuranceOutputOrusDiscountAmount,
        })
      }
    }
  }

  getMonthlyOrusDiscountAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        switch (paymentRecurrence) {
          case 'monthly': {
            return this.getMonthlyAmountForMonthlyRecurrence({
              isAnnuityFirstMonth,
              isContractFirstMonth,
              getInsuranceOutputAmount: this.getInsuranceOutputOrusDiscountAmount,
            })
          }
          case 'yearly': {
            return divideByNumber(
              this.getYearlyOrusDiscountAmountForPaymentRecurrence({
                paymentRecurrence,
                isAnnuityFirstMonth,
                isContractFirstMonth,
              }),
              monthsInAYear,
            )
          }
        }
      }
    }
  }
  getYearlyOverchargeAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    switch (this.quote.quoteModelVersion) {
      case 1: {
        return this.getYearlyAmountForPaymentRecurrence({
          paymentRecurrence,
          isAnnuityFirstMonth,
          isContractFirstMonth,
          getInsuranceOutputAmount: this.getInsuranceOutputOverchargeAmount,
        })
      }
    }
  }
  //#region Payment recurrence methods
  /*
   * This method is used to get the monthly amount for a given insurance output.
   * It manages specific payment terms and divides by 12
   */
  getMonthlyzedAmount(
    insuranceOutput: InsuranceOutput,
    getInsuranceOutputAmount: (insuranceOutput: InsuranceOutput) => Amount,
  ): Amount {
    if (insuranceOutput.paymentTerms.type === 'oneTime' || insuranceOutput.paymentTerms.type === 'yearly') {
      return getInsuranceOutputAmount(insuranceOutput)
    }
    return divideByNumber(getInsuranceOutputAmount(insuranceOutput), monthsInAYear)
  }

  /*
   * This method is used to get the monthly amount (divided by 12) for monthly payment recurrence for a given insurance output.
   */
  getMonthlyAmountForMonthlyRecurrence({
    isAnnuityFirstMonth,
    isContractFirstMonth,
    getInsuranceOutputAmount,
  }: {
    getInsuranceOutputAmount: (insuranceOutput: InsuranceOutput) => Amount
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
  }): Amount {
    const monthlyInsuranceOutputs = this.quote.monthly.billables
    const monthlyTotal = addAmounts(
      ...Object.values<InsuranceOutput>(monthlyInsuranceOutputs).map((insuranceOutput) =>
        this.shouldSkipInsuranceOutput(insuranceOutput, isContractFirstMonth, isAnnuityFirstMonth)
          ? newAmount(0)
          : this.getMonthlyzedAmount(insuranceOutput, getInsuranceOutputAmount),
      ),
    )
    return monthlyTotal
  }

  getInsuranceOutputsMonthlyAmountForMonthlyRecurrence({
    isAnnuityFirstMonth,
    isContractFirstMonth,
    getInsuranceOutputAmount,
    insuranceOutputs,
  }: {
    getInsuranceOutputAmount: (insuranceOutput: InsuranceOutput) => Amount
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
    insuranceOutputs: InsuranceOutput[]
  }): Amount {
    const monthlyTotal = addAmounts(
      ...insuranceOutputs.map((insuranceOutput) =>
        this.shouldSkipInsuranceOutput(insuranceOutput, isContractFirstMonth, isAnnuityFirstMonth)
          ? newAmount(0)
          : this.getMonthlyzedAmount(insuranceOutput, getInsuranceOutputAmount),
      ),
    )
    return monthlyTotal
  }

  getYearlyAmountForPaymentRecurrence({
    paymentRecurrence,
    isAnnuityFirstMonth,
    isContractFirstMonth,
    getInsuranceOutputAmount,
  }: {
    paymentRecurrence: PaymentRecurrence
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
    getInsuranceOutputAmount: (insuranceOutput: InsuranceOutput) => Amount
  }): Amount {
    const yearlyInsuranceOutputs = this.quote[paymentRecurrence].billables
    const yearlyTotal = addAmounts(
      ...Object.values<InsuranceOutput>(yearlyInsuranceOutputs).map((insuranceOutput) =>
        this.shouldSkipInsuranceOutput(insuranceOutput, isContractFirstMonth, isAnnuityFirstMonth)
          ? newAmount(0)
          : getInsuranceOutputAmount(insuranceOutput),
      ),
    )

    return yearlyTotal
  }

  getInsuranceOutputsYearlyAmountForPaymentRecurrence({
    isAnnuityFirstMonth,
    isContractFirstMonth,
    getInsuranceOutputAmount,
    insuranceOutputs,
  }: {
    isAnnuityFirstMonth: boolean
    isContractFirstMonth: boolean
    getInsuranceOutputAmount: (insuranceOutput: InsuranceOutput) => Amount
    insuranceOutputs: InsuranceOutput[]
  }): Amount {
    const yearlyTotal = addAmounts(
      ...insuranceOutputs.map((insuranceOutput) =>
        this.shouldSkipInsuranceOutput(insuranceOutput, isContractFirstMonth, isAnnuityFirstMonth)
          ? newAmount(0)
          : getInsuranceOutputAmount(insuranceOutput),
      ),
    )

    return yearlyTotal
  }

  //#endregion

  //#region Insurance Output Amounts Core methods
  getInsuranceOutputExTaxAmount(insuranceOutput: InsuranceOutput): Amount {
    return insuranceOutput.yearlyExTax
  }

  getInsuranceOutputInsuredExTaxAmount(insuranceOutput: InsuranceOutput): Amount {
    return getInsuranceOutputAmount(insuranceOutput, { type: 'insuredExTax' })
  }

  getInsuranceOutputTaxesAmount(insuranceOutput: InsuranceOutput): Amount {
    return getInsuranceOutputAmount(insuranceOutput, { type: 'taxes' })
  }

  getInsuranceOutputInstallmentFeeAmount(insuranceOutput: InsuranceOutput): Amount {
    return getInsuranceOutputAmount(insuranceOutput, { type: 'orusInstallmentFee' })
  }

  getInsuranceOutputOrusManagementFeeAmount(insuranceOutput: InsuranceOutput): Amount {
    return getInsuranceOutputAmount(insuranceOutput, { type: 'orusManagementFee' })
  }

  getInsuranceOutputOrusDiscountAmount(insuranceOutput: InsuranceOutput): Amount {
    return getInsuranceOutputAmount(insuranceOutput, { type: 'orusDiscountAmount' })
  }

  getInsuranceOutputOverchargeAmount(insuranceOutput: InsuranceOutput): Amount {
    return getInsuranceOutputAmount(insuranceOutput, { type: 'overchargeAmount' })
  }

  getInsuranceOutputPartnerManagementFeeAmount(insuranceOutput: InsuranceOutput): Amount {
    return getInsuranceOutputAmount(insuranceOutput, { type: 'partnerManagementFee' })
  }

  getInsuranceOutputTotalAmount(insuranceOutput: InsuranceOutput): Amount {
    return getInsuranceOutputAmount(insuranceOutput)
  }

  //#endregion
}
